import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  Breadcrumbs,
  Divider,
  Button,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import StarIcon from "@mui/icons-material/Star";
import CategoryIcon from "@mui/icons-material/Category";
import { getBannerProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";
import PromotionsSectionCompo from "./PromotionsSectionCompo";

const PromotionsPage = ({ bannerId: propBannerId }) => {
  const { id: paramId } = useParams();
  const bannerId = propBannerId || paramId;
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  useEffect(() => {
    document.title = 'DIDA MAX — Promotions';
  }, []);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(Boolean(bannerId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bannerId) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getBannerProducts(bannerId);
        const data = res && (res.data ?? res);
        setBanner(data || null);
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [bannerId]);

  const getTitle = (b) => (lang === "ar" ? b.title_ar || b.title_en : b.title_en || b.title_ar);
  const getDesc = (b) => (lang === "ar" ? b.description_ar || b.description_en : b.description_en || b.description_ar);

  if (!bannerId) return null;

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="error" variant="h6" fontWeight={600}>
          {t("error_loading_banner", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل العرض" : "Error loading banner" })}
        </Typography>
      </Box>
    );
  }

  const categories = Array.isArray(banner?.categories) ? banner.categories : [];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Show all promotions at top */}
      <PromotionsSectionCompo />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />}
          sx={{ mb: 4 }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <HomeIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {lang === "ar" ? "الرئيسية" : "Home"}
            </Typography>
          </Link>
          <Typography variant="body2" color="text.primary" fontWeight={700}>
            {getTitle(banner || {})}
          </Typography>
        </Breadcrumbs>

        {/* Banner Header Card */}
        <Card
          sx={{
            mb: 5,
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background:
                "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              p: 4,
              background:
                "linear-gradient(135deg, rgba(213, 170, 47, 0.03) 0%, rgba(230, 199, 106, 0.03) 100%)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                flexShrink: 0,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: -8,
                  background:
                    "linear-gradient(135deg, rgba(213, 170, 47, 0.2), rgba(230, 199, 106, 0.2))",
                  borderRadius: 3,
                  zIndex: 0,
                  opacity: 0.5,
                },
              }}
            >
              <Avatar
                src={getImageUrl(banner?.image)}
                alt={getTitle(banner || {})}
                sx={{
                  width: { xs: 120, md: 160 },
                  height: { xs: 120, md: 160 },
                  position: "relative",
                  zIndex: 1,
                  border: "4px solid #fff",
                  boxShadow: "0 8px 24px rgba(213, 170, 47, 0.2)",
                }}
                variant="rounded"
              />
            </Box>
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography
                variant="h3"
                fontWeight={800}
                gutterBottom
                sx={{
                  background:
                    "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 2,
                }}
              >
                {getTitle(banner || {})}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 700 }}>
                {getDesc(banner || {})}
              </Typography>
              {banner?.end_date && (
                <Chip
                  label={
                    lang === "ar"
                      ? `العرض ساري حتى ${banner.end_date}`
                      : `Valid until ${banner.end_date}`
                  }
                  sx={{
                    mt: 2,
                    alignSelf: "flex-start",
                    bgcolor: "rgba(213, 170, 47, 0.1)",
                    color: "primary.dark",
                    fontWeight: 700,
                    border: "1px solid rgba(213, 170, 47, 0.3)",
                  }}
                />
              )}
            </Box>
          </Box>
        </Card>

        {/* Categories horizontal bar */}
        {categories.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <CategoryIcon sx={{ color: "primary.main", fontSize: 28 }} />
              <Typography variant="h5" fontWeight={700}>
                {lang === "ar" ? "التصنيفات المتاحة" : "Available Categories"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(213, 170, 47, 0.3)",
                  borderRadius: 4,
                },
              }}
            >
              {categories.map((cat, index) => (
                <Card
                  key={cat.id}
                  component={Link}
                  to={`/category/${cat.id}`}
                  sx={{
                    minWidth: 200,
                    flexShrink: 0,
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(213, 170, 47, 0.15)",
                      borderColor: "primary.main",
                    },
                    animation: `slideIn 0.5s ease-out ${index * 0.1}s both`,
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-20px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                    <Avatar
                      src={getImageUrl(cat.image)}
                      alt={lang === "ar" ? cat.name_ar : cat.name_en}
                      variant="rounded"
                      sx={{
                        width: 64,
                        height: 64,
                        border: "2px solid rgba(213, 170, 47, 0.2)",
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                        {lang === "ar" ? cat.name_ar : cat.name_en}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {lang === "ar" ? cat.desc_ar : cat.desc_en}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Products grouped by category */}
        {categories.map((cat, catIndex) => {
          const products = Array.isArray(cat.products) ? cat.products : [];
          if (products.length === 0) return null;
          return (
            <Box key={cat.id} sx={{ mb: 7 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 4,
                  pb: 2,
                  borderBottom: "2px solid",
                  borderImage: "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 32,
                      bgcolor: "primary.main",
                      borderRadius: 1,
                    }}
                  />
                  {lang === "ar" ? cat.name_ar : cat.name_en}
                </Typography>
                <Button
                  component={Link}
                  to={`/category/${cat.id}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    fontWeight: 700,
                    "&:hover": {
                      bgcolor: "rgba(213, 170, 47, 0.08)",
                    },
                  }}
                >
                  {lang === "ar" ? "عرض الكل" : "View All"}
                </Button>
              </Box>

              <Grid container spacing={3}>
                {products.map((product, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      sx={{
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                          "& .product-image": {
                            transform: "scale(1.08)",
                          },
                        },
                        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
                        "@keyframes fadeInUp": {
                          from: {
                            opacity: 0,
                            transform: "translateY(20px)",
                          },
                          to: {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        },
                      }}
                    >
                      <CardActionArea
                        component={Link}
                        to={`/product/${product.id}`}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                        }}
                      >
                        <Box sx={{ position: "relative", overflow: "hidden" }}>
                          <CardMedia
                            component="img"
                            image={getImageUrl(
                              product.main_photo ||
                                (product.images && product.images[0] && product.images[0].image) ||
                                ""
                            )}
                            alt={lang === "ar" ? product.name_ar : product.name_en}
                            className="product-image"
                            sx={{
                              width: "100%",
                              height: 220,
                              objectFit: "cover",
                              transition: "transform 0.4s ease",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.3) 100%)",
                            }}
                          />
                        </Box>

                        <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 180 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                              mb: 1,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4,
                              height: "2.8em",
                            }}
                          >
                            {lang === "ar" ? product.name_ar : product.name_en}
                          </Typography>

                          {product.reviews_summary && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: "auto",
                              }}
                            >
                              {product.reviews_summary.average_rating ? (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      bgcolor: "rgba(213, 170, 47, 0.1)",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                    }}
                                  >
                                    <StarIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                    <Typography variant="body2" fontWeight={700} color="primary.dark">
                                      {product.reviews_summary.average_rating}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    ({product.reviews_summary.total_reviews || 0}{" "}
                                    {lang === "ar" ? "تقييم" : "reviews"})
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.disabled" fontWeight={600}>
                                  {t("no_rating", {
                                    defaultValue: lang === "ar" ? "بدون تقييم" : "No rating",
                                  })}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}

        {/* Empty State */}
        {categories.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              {lang === "ar"
                ? "لا توجد منتجات متاحة في هذا العرض حالياً"
                : "No products available in this promotion"}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PromotionsPage;
